import { useState, useEffect } from 'react';
import axios from 'axios';
import { ShieldAlert, Smartphone, Fingerprint, ArrowRight, X } from 'lucide-react';
import StandardModal from './StandardModal';
import { PrimaryButton, SecondaryButton } from './Buttons';
import { API_URL } from '../config/api';

const SNOOZE_KEY = 'sa-2fa-nag-snoozed-until';

/**
 * Post-login interstitial that pushes BookingFlows team members off email-only
 * 2FA and onto an authenticator app / security key. Mandatory-feel but skippable
 * (24h snooze) so nobody is ever locked out mid-incident. Fires once per session
 * load when the account has no strong factor enrolled.
 */
const SecureAccountPrompt = ({ onGoToSecurity }) => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/v2/super-admin/security/status`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` },
        });
        if (cancelled) return;
        const snoozed = Number(localStorage.getItem(SNOOZE_KEY) || 0);
        if (res.data?.email_otp_active && Date.now() > snoozed) setShow(true);
      } catch {
        /* non-blocking */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const snooze = () => {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
    setShow(false);
  };

  const goSecure = () => {
    setShow(false);
    onGoToSecurity();
  };

  return (
    <StandardModal
      isOpen={show}
      onClose={snooze}
      title="Secure your Platform Admin account"
      size="md"
      showCloseButton={false}
      closeOnOutsideClick={false}
      footer={
        <>
          <SecondaryButton onClick={snooze} className="flex-1" data-testid="secure-account-later">
            <X className="w-4 h-4 mr-1.5" />Remind me tomorrow
          </SecondaryButton>
          <PrimaryButton onClick={goSecure} className="flex-1" data-testid="secure-account-go">
            Set up now<ArrowRight className="w-4 h-4 ml-1.5" />
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-4" data-testid="secure-account-prompt">
        <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
          <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            Your account still signs in with <span className="font-semibold">email codes only</span>.
            BookingFlows now requires every admin to add a stronger, phishing-resistant factor.
          </p>
        </div>
        <p className="text-sm text-slate-600">Pick one (takes under a minute):</p>
        <div className="space-y-2.5">
          <div className="flex items-start gap-3 p-3 rounded-xl border border-slate-200">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
              <Smartphone className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <div className="text-sm font-medium text-slate-800">Authenticator app <span className="text-xs font-normal text-emerald-600">· recommended</span></div>
              <div className="text-xs text-slate-500">Google Authenticator, Authy, 1Password — a rolling 6-digit code.</div>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 rounded-xl border border-slate-200">
            <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
              <Fingerprint className="w-4 h-4 text-indigo-600" />
            </div>
            <div>
              <div className="text-sm font-medium text-slate-800">Security key or passkey</div>
              <div className="text-xs text-slate-500">YubiKey, Face ID / Touch ID, or a synced passkey — the strongest option.</div>
            </div>
          </div>
        </div>
        <p className="text-xs text-slate-400">Once you add one, email codes stop being accepted for sign-in and dangerous actions will ask for two different factors.</p>
      </div>
    </StandardModal>
  );
};

export default SecureAccountPrompt;
